/**
 * Activity-log words (action verbs, changed-field names, result values) in zh / en / ms. The POS and admin activity logs both
 * render through these, so "lalamove_resubmit", "is_available" or "failed" never reach a screen as raw codes.
 * Unknown values return undefined so callers can fall back to their own text.
 */
import { orderStatusLabel, riderStatusLabel } from "./statusLabels.js";
import { cancelReasonLabel } from "./cancelReason.js";
const lang3 = (lang) => (lang.startsWith("ms") ? "ms" : lang.startsWith("en") ? "en" : "zh");
const key = (s) => s.trim().toLowerCase().replace(/[\s-]+/g, "_");
const VERB = {
    lalamove_resubmit: { zh: "重新提交 Lalamove", en: "Resubmitted to Lalamove", ms: "Hantar semula ke Lalamove" },
    lalamove_booked: { zh: "已叫 Lalamove，正在找骑手", en: "Lalamove booked — finding a rider", ms: "Lalamove ditempah — mencari penunggang" },
    lalamove_failed: { zh: "叫 Lalamove 失败", en: "Lalamove booking failed", ms: "Tempahan Lalamove gagal" },
    lalamove_dispatch: { zh: "Lalamove 派送", en: "Lalamove dispatch", ms: "Penghantaran Lalamove" },
    lalamove_quote: { zh: "Lalamove 报价", en: "Lalamove quote", ms: "Sebut harga Lalamove" },
    lalamove_cancel: { zh: "Lalamove 取消", en: "Lalamove cancel", ms: "Pembatalan Lalamove" },
    assign_rider: { zh: "指派骑手", en: "Rider assigned", ms: "Penunggang ditugaskan" },
    mark_paid: { zh: "标记已付款", en: "Marked paid", ms: "Ditanda dibayar" },
    admin_cancel: { zh: "后台取消", en: "Cancelled by admin", ms: "Dibatalkan oleh admin" },
    walkin_create: { zh: "新增现场订单", en: "Walk-in order created", ms: "Pesanan walk-in dicipta" },
    rider_status: { zh: "骑手状态", en: "Rider status", ms: "Status penunggang" },
};
const FIELD = {
    is_available: { zh: "是否上架", en: "Available", ms: "Tersedia" },
    is_active: { zh: "启用状态", en: "Active", ms: "Aktif" },
    is_open: { zh: "营业状态", en: "Open", ms: "Dibuka" },
    is_featured: { zh: "置顶推荐", en: "Featured", ms: "Pilihan" },
    name: { zh: "名称", en: "Name", ms: "Nama" },
    price: { zh: "价格", en: "Price", ms: "Harga" },
    description: { zh: "描述", en: "Description", ms: "Penerangan" },
    image_url: { zh: "图片", en: "Image", ms: "Imej" },
    image_url_wide: { zh: "横版图片", en: "Wide image", ms: "Imej lebar" },
    sort_order: { zh: "排序", en: "Sort order", ms: "Susunan" },
    daily_stock: { zh: "每日库存", en: "Daily stock", ms: "Stok harian" },
    offer_price: { zh: "优惠价", en: "Offer price", ms: "Harga tawaran" },
    category_ids: { zh: "分类", en: "Categories", ms: "Kategori" },
    option_groups: { zh: "选项组", en: "Option groups", ms: "Kumpulan pilihan" },
    available_from: { zh: "供应开始", en: "Available from", ms: "Tersedia dari" },
    available_to: { zh: "供应结束", en: "Available until", ms: "Tersedia hingga" },
    // ── activity-log meta keys (what changed / on what) ──
    orderCode: { zh: "订单号", en: "Order", ms: "Pesanan" },
    prevStatus: { zh: "原状态", en: "Previous status", ms: "Status sebelum" },
    status: { zh: "状态", en: "Status", ms: "Status" },
    role: { zh: "角色", en: "Role", ms: "Peranan" },
    estMinutes: { zh: "预计分钟", en: "Est. minutes", ms: "Anggaran minit" },
    amount: { zh: "金额", en: "Amount", ms: "Jumlah" },
    method: { zh: "方式", en: "Method", ms: "Kaedah" },
    reason: { zh: "原因", en: "Reason", ms: "Sebab" },
    note: { zh: "备注", en: "Note", ms: "Nota" },
    lineTotal: { zh: "金额", en: "Amount", ms: "Jumlah" },
    autoRefundId: { zh: "自动退款", en: "Auto refund", ms: "Bayaran balik auto" },
    result: { zh: "结果", en: "Result", ms: "Keputusan" },
    lalamoveCancelResult: { zh: "Lalamove 取消结果", en: "Lalamove cancel result", ms: "Keputusan batal Lalamove" },
    fields: { zh: "修改字段", en: "Changed fields", ms: "Medan diubah" },
    changes: { zh: "修改项", en: "Changes", ms: "Perubahan" },
    driverName: { zh: "骑手", en: "Rider", ms: "Penunggang" },
    riderName: { zh: "骑手", en: "Rider", ms: "Penunggang" },
    code: { zh: "券码", en: "Code", ms: "Kod" },
    type: { zh: "类型", en: "Type", ms: "Jenis" },
    value: { zh: "面值", en: "Value", ms: "Nilai" },
    date: { zh: "日期", en: "Date", ms: "Tarikh" },
    email: { zh: "邮箱", en: "Email", ms: "E-mel" },
    phone: { zh: "手机号", en: "Phone", ms: "Telefon" },
    label: { zh: "名称", en: "Name", ms: "Nama" },
    enabled: { zh: "启用", en: "Enabled", ms: "Didayakan" },
    count: { zh: "数量", en: "Count", ms: "Bilangan" },
    platform: { zh: "平台", en: "Platform", ms: "Platform" },
    provider: { zh: "服务商", en: "Provider", ms: "Penyedia" },
    path: { zh: "途径", en: "Path", ms: "Laluan" },
    decision: { zh: "决定", en: "Decision", ms: "Keputusan" },
    transfer_method: { zh: "退款方式", en: "Refund method", ms: "Kaedah bayaran balik" },
    stall_name: { zh: "档口", en: "Stall", ms: "Gerai" },
    stall_id: { zh: "档口", en: "Stall", ms: "Gerai" },
    customerCount: { zh: "顾客数", en: "Customers", ms: "Pelanggan" },
    generated: { zh: "系统生成新 PIN", en: "New PIN generated", ms: "PIN baharu dijana" },
    title: { zh: "标题", en: "Title", ms: "Tajuk" },
    group_id: { zh: "选项组", en: "Option group", ms: "Kumpulan pilihan" },
    item_id: { zh: "菜品", en: "Item", ms: "Item" },
    item_name: { zh: "菜品", en: "Item", ms: "Item" },
    staff_auto_created: { zh: "自动创建员工", en: "Staff auto-created", ms: "Staf dicipta auto" },
    total: { zh: "总额", en: "Total", ms: "Jumlah" },
    extra_charge: { zh: "加价", en: "Surcharge", ms: "Caj tambahan" },
    choices: { zh: "选项", en: "Choices", ms: "Pilihan" },
    from: { zh: "原值", en: "From", ms: "Dari" },
    to: { zh: "新值", en: "To", ms: "Kepada" },
    fee: { zh: "运费", en: "Fee", ms: "Yuran" },
    success: { zh: "是否成功", en: "Success", ms: "Berjaya" },
    item_count: { zh: "菜品数量", en: "Items", ms: "Bilangan item" },
    webhookUrl: { zh: "回调地址", en: "Webhook URL", ms: "URL webhook" },
    // ── settings / stall / staff / voucher / provider fields ──
    address: { zh: "地址", en: "Address", ms: "Alamat" },
    accepts_cash: { zh: "接受现金", en: "Accepts cash", ms: "Terima tunai" },
    addon_commission_percent: { zh: "加料佣金比例", en: "Add-on commission %", ms: "Komisen tambahan %" },
    addon_markup_percent: { zh: "加料加价率", en: "Add-on markup %", ms: "Markup tambahan %" },
    app_version_label: { zh: "应用版本号", en: "App version label", ms: "Label versi apl" },
    auto_accept: { zh: "自动接单", en: "Auto-accept", ms: "Terima auto" },
    auto_grant_trigger: { zh: "自动发放条件", en: "Auto-grant trigger", ms: "Pencetus beri auto" },
    bank_refund_enabled: { zh: "银行退款", en: "Bank refunds", ms: "Bayaran balik bank" },
    cash_refund_enabled: { zh: "现金退款", en: "Cash refunds", ms: "Bayaran balik tunai" },
    base_url: { zh: "接口地址", en: "Base URL", ms: "URL asas" },
    charge_sst: { zh: "收取 SST", en: "Charge SST", ms: "Caj SST" },
    commission_flat_fee: { zh: "固定佣金", en: "Flat commission", ms: "Komisen tetap" },
    commission_percent: { zh: "佣金比例", en: "Commission %", ms: "Komisen %" },
    commission_threshold: { zh: "佣金门槛", en: "Commission threshold", ms: "Ambang komisen" },
    cover_image: { zh: "封面图片", en: "Cover image", ms: "Imej muka depan" },
    coverage_km: { zh: "配送范围 (公里)", en: "Coverage (km)", ms: "Liputan (km)" },
    daily_limit_per_customer: { zh: "每人每日限用", en: "Daily limit per customer", ms: "Had harian setiap pelanggan" },
    default_option_groups: { zh: "默认选项组", en: "Default option groups", ms: "Kumpulan pilihan lalai" },
    delivery_control_enabled: { zh: "配送管控", en: "Delivery control", ms: "Kawalan penghantaran" },
    delivery_fee_default: { zh: "默认外送费", en: "Default delivery fee", ms: "Yuran penghantaran lalai" },
    delivery_radius_km: { zh: "外送半径 (公里)", en: "Delivery radius (km)", ms: "Radius penghantaran (km)" },
    discount_target: { zh: "折扣对象", en: "Discount target", ms: "Sasaran diskaun" },
    display_name: { zh: "显示名称", en: "Display name", ms: "Nama paparan" },
    email_settings: { zh: "邮件设置", en: "Email settings", ms: "Tetapan e-mel" },
    email_template_settings: { zh: "邮件模板", en: "Email templates", ms: "Templat e-mel" },
    emoji: { zh: "图标", en: "Emoji", ms: "Emoji" },
    endpoint: { zh: "接口", en: "Endpoint", ms: "Titik akhir" },
    icon_key: { zh: "图标", en: "Icon", ms: "Ikon" },
    icon_url: { zh: "图标", en: "Icon", ms: "Ikon" },
    input_type: { zh: "输入方式", en: "Input type", ms: "Jenis input" },
    is_dine_in: { zh: "堂食", en: "Dine-in", ms: "Makan di sini" },
    is_sandbox: { zh: "测试环境", en: "Sandbox", ms: "Kotak pasir" },
    kitchen_paused: { zh: "暂停接单", en: "Kitchen paused", ms: "Dapur dijeda" },
    kitchen_paused_minutes: { zh: "暂停分钟数", en: "Pause minutes", ms: "Minit jeda" },
    logo_url: { zh: "标志", en: "Logo", ms: "Logo" },
    max_cap: { zh: "上限", en: "Max cap", ms: "Had maksimum" },
    max_select: { zh: "最多可选", en: "Max select", ms: "Pilihan maksimum" },
    min_select: { zh: "最少可选", en: "Min select", ms: "Pilihan minimum" },
    menu_item_id: { zh: "菜品", en: "Item", ms: "Item" },
    merchant_id: { zh: "商户号", en: "Merchant ID", ms: "ID pedagang" },
    min_order: { zh: "最低消费", en: "Minimum order", ms: "Pesanan minimum" },
    no_minimum: { zh: "无最低消费", en: "No minimum", ms: "Tiada minimum" },
    notification_settings: { zh: "通知设置", en: "Notification settings", ms: "Tetapan pemberitahuan" },
    notifications_enabled: { zh: "通知", en: "Notifications", ms: "Pemberitahuan" },
    otp_settings: { zh: "验证码设置", en: "OTP settings", ms: "Tetapan OTP" },
    payment_qr_url: { zh: "收款二维码", en: "Payment QR", ms: "QR pembayaran" },
    permissions: { zh: "权限", en: "Permissions", ms: "Kebenaran" },
    prep_time: { zh: "备餐时间 (分钟)", en: "Prep time (min)", ms: "Masa sedia (min)" },
    price_markup_flat_fee: { zh: "固定手续费", en: "Flat markup", ms: "Markup tetap" },
    price_markup_percent: { zh: "手续费百分比", en: "Markup %", ms: "Markup %" },
    price_markup_threshold: { zh: "价格门槛", en: "Markup threshold", ms: "Ambang markup" },
    printer_settings: { zh: "打印机设置", en: "Printer settings", ms: "Tetapan pencetak" },
    qr_image_url: { zh: "二维码", en: "QR image", ms: "Imej QR" },
    registration_settings: { zh: "注册设置", en: "Registration settings", ms: "Tetapan pendaftaran" },
    rider_tracking_enabled: { zh: "骑手追踪", en: "Rider tracking", ms: "Penjejakan penunggang" },
    skip_default_groups: { zh: "跳过默认选项组", en: "Skip default groups", ms: "Langkau kumpulan lalai" },
    sms_template_settings: { zh: "短信模板", en: "SMS templates", ms: "Templat SMS" },
    special_note: { zh: "特别备注", en: "Special note", ms: "Nota khas" },
    split_cost: { zh: "档口分成", en: "Stall split", ms: "Bahagian gerai" },
    sst_rate: { zh: "SST 税率", en: "SST rate", ms: "Kadar SST" },
    table_number: { zh: "桌号", en: "Table no.", ms: "No. meja" },
    tablet_label: { zh: "平板名称", en: "Tablet label", ms: "Label tablet" },
    total_uses: { zh: "总使用次数", en: "Total uses", ms: "Jumlah guna" },
    transfer_time: { zh: "转账时间", en: "Transfer time", ms: "Masa pindahan" },
    valid_from: { zh: "开始日期", en: "Valid from", ms: "Sah dari" },
    valid_to: { zh: "结束日期", en: "Valid until", ms: "Sah hingga" },
    claim_required: { zh: "需领取", en: "Claim required", ms: "Perlu tuntut" },
    day_of_week: { zh: "星期", en: "Day of week", ms: "Hari" },
    fee_config: { zh: "费用设置", en: "Fee settings", ms: "Tetapan yuran" },
    lat: { zh: "纬度", en: "Latitude", ms: "Latitud" },
    lng: { zh: "经度", en: "Longitude", ms: "Longitud" },
    radius: { zh: "半径", en: "Radius", ms: "Radius" },
    mode: { zh: "模式", en: "Mode", ms: "Mod" },
    scope: { zh: "范围", en: "Scope", ms: "Skop" },
    reference: { zh: "参考号", en: "Reference", ms: "Rujukan" },
    qty: { zh: "数量", en: "Qty", ms: "Kuantiti" },
    items: { zh: "菜品", en: "Items", ms: "Item" },
    category: { zh: "分类", en: "Category", ms: "Kategori" },
    tag: { zh: "标签", en: "Tag", ms: "Tag" },
    tag_color: { zh: "标签颜色", en: "Tag colour", ms: "Warna tag" },
    link_url: { zh: "链接", en: "Link", ms: "Pautan" },
    bg_gradient: { zh: "背景色", en: "Background", ms: "Latar" },
    original_price: { zh: "原价", en: "Original price", ms: "Harga asal" },
    bundle_price: { zh: "套餐价", en: "Bundle price", ms: "Harga set" },
    applicable_for: { zh: "适用方式", en: "Applies to", ms: "Terpakai untuk" },
    app_scope: { zh: "应用", en: "App", ms: "Apl" },
    doc_type: { zh: "文档类型", en: "Document type", ms: "Jenis dokumen" },
    key: { zh: "开关", en: "Flag", ms: "Bendera" },
    isActive: { zh: "启用状态", en: "Active", ms: "Aktif" },
    customer_name: { zh: "顾客姓名", en: "Customer name", ms: "Nama pelanggan" },
    customer_phone: { zh: "顾客电话", en: "Customer phone", ms: "Telefon pelanggan" },
    api_key: { zh: "接口密钥", en: "API key", ms: "Kunci API" },
    api_secret: { zh: "接口密钥", en: "API secret", ms: "Rahsia API" },
    webhook_secret: { zh: "回调密钥", en: "Webhook secret", ms: "Rahsia webhook" },
    config_json: { zh: "接口配置", en: "Configuration", ms: "Konfigurasi" },
    password: { zh: "密码", en: "Password", ms: "Kata laluan" },
    pin: { zh: "PIN", en: "PIN", ms: "PIN" },
    smtpPass: { zh: "邮箱密码", en: "SMTP password", ms: "Kata laluan SMTP" },
    resendApiKey: { zh: "邮件接口密钥", en: "Email API key", ms: "Kunci API e-mel" },
};
const RESULT = {
    failed: { zh: "失败", en: "Failed", ms: "Gagal" },
    skipped: { zh: "已跳过", en: "Skipped", ms: "Dilangkau" },
    cancelled: { zh: "已取消", en: "Cancelled", ms: "Dibatalkan" },
    success: { zh: "成功", en: "Success", ms: "Berjaya" },
    true: { zh: "是", en: "Yes", ms: "Ya" },
    false: { zh: "否", en: "No", ms: "Tidak" },
};
const VALUE = {
    pos_operator: { zh: "POS 操作员", en: "POS operator", ms: "Operator POS" },
    stall_staff: { zh: "档口员工", en: "Stall staff", ms: "Staf gerai" },
    admin: { zh: "管理员", en: "Admin", ms: "Admin" },
    superadmin: { zh: "超级管理员", en: "Super admin", ms: "Super admin" },
    pickup: { zh: "自取", en: "Pickup", ms: "Ambil sendiri" },
    delivery: { zh: "外送", en: "Delivery", ms: "Penghantaran" },
    percentage: { zh: "百分比", en: "Percentage", ms: "Peratusan" },
    fixed: { zh: "固定金额", en: "Fixed amount", ms: "Jumlah tetap" },
    partial: { zh: "部分退款", en: "Partial refund", ms: "Bayaran balik separa" },
    full: { zh: "全额退款", en: "Full refund", ms: "Bayaran balik penuh" },
    manual: { zh: "手动", en: "Manual", ms: "Manual" },
    approve: { zh: "批准", en: "Approved", ms: "Diluluskan" },
    approved: { zh: "批准", en: "Approved", ms: "Diluluskan" },
    reject: { zh: "拒绝", en: "Rejected", ms: "Ditolak" },
    rejected: { zh: "拒绝", en: "Rejected", ms: "Ditolak" },
    lalamove: { zh: "Lalamove", en: "Lalamove", ms: "Lalamove" },
    pending: { zh: "待处理", en: "Pending", ms: "Menunggu" },
};
const pick = (m, v, lang) => (v ? (m[v] ?? m[key(v)])?.[lang3(lang)] : undefined);
// "order.<status>" actions (preparing, ready, out_for_delivery, in_transit, delivered, completed, cancelled) read as the status in words
export const activityVerbLabel = (verb, lang = "zh") => pick(VERB, verb, lang) ?? orderStatusLabel(verb, lang);
export const activityFieldLabel = (field, lang = "zh") => pick(FIELD, field, lang);
export const activityResultLabel = (value, lang = "zh") => pick(RESULT, value, lang);
/** Payment methods as words ("cash" / "CASH" → 现金). */
const METHOD = {
    cash: { zh: "现金", en: "Cash", ms: "Tunai" },
    card: { zh: "信用卡 / 借记卡", en: "Credit / Debit Card", ms: "Kad Kredit / Debit" },
    transfer: { zh: "转账", en: "Transfer", ms: "Pindahan" },
    fpx: { zh: "网上银行 (FPX)", en: "Online banking (FPX)", ms: "Perbankan dalam talian (FPX)" },
    online_banking: { zh: "网上银行", en: "Online banking", ms: "Perbankan dalam talian" },
    fiuu: { zh: "网上支付 (Fiuu)", en: "Online payment (Fiuu)", ms: "Pembayaran dalam talian (Fiuu)" },
    ewallet: { zh: "电子钱包", en: "E-wallet", ms: "E-dompet" },
    grabpay: { zh: "GrabPay", en: "GrabPay", ms: "GrabPay" },
};
export const payMethodLabel = (method, lang = "zh") => pick(METHOD, method, lang);
/** Fields whose value must never be shown: the log says only that they were changed. */
const SECRET = /(^|_)(api_?key|api_?secret|secret|password|pass|pin|token|totp|webhook_secret|config_json)(_|$)|apikey|smtppass|totpcode/i;
const CHANGED = { zh: "已修改", en: "changed", ms: "diubah" };
const FROM_TO = (a, b, lang) => (lang3(lang) === "zh" ? `从 ${a} 改为 ${b}` : lang3(lang) === "ms" ? `dari ${a} kepada ${b}` : `${a} → ${b}`);
const UNIT = [
    [/(^|_)rate$/, (n) => `${+(n * 100).toFixed(2)}%`],
    [/percent$/, (n) => `${n}%`],
    [/_km$|^coverage_km$/, (n, l) => `${n} ${lang3(l) === "zh" ? "公里" : "km"}`],
    [/minutes$|^prep_time$|^estMinutes$/, (n, l) => `${n} ${lang3(l) === "zh" ? "分钟" : lang3(l) === "ms" ? "minit" : "min"}`],
    [/(^|_)(fee|price|amount|total|threshold|fee_default|min_order|max_cap|lineTotal)$|^delivery_fee_default$|flat_fee$/i, (n) => `RM ${n.toFixed(2)}`],
];
/**
 * Turn an activity-log `meta` object into one readable line. Used by BOTH the admin and the POS logs so neither shows raw
 * keys ("delivery_radius_km"), raw values ("CASH", "stall_classics"), nested JSON, or secrets. `sep` joins the parts.
 */
export function activityMetaText(meta, lang, extraValue) {
    if (!meta)
        return "";
    const l = lang3(lang);
    const ID_KEY = /(^|_)id$|Id$/;
    const OPAQUE_ID = /^(c[a-z0-9]{24}|(stall|mi|mo|cat|opt|grp)_[a-z0-9_]+)$/;
    const label = (k) => activityFieldLabel(k, lang) ?? k.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[._]/g, " ");
    const scalar = (k, x) => {
        const custom = extraValue?.(k, x);
        if (custom !== undefined)
            return custom;
        if (x === null || x === undefined)
            return "—";
        if (typeof x === "boolean")
            return activityResultLabel(String(x), lang) ?? String(x);
        if (typeof x === "number" || (typeof x === "string" && x.trim() !== "" && !isNaN(Number(x)) && UNIT.some(([re]) => re.test(k)))) {
            const n = Number(x);
            const u = UNIT.find(([re]) => re.test(k));
            return u ? u[1](n, lang) : String(x);
        }
        const v = String(x);
        return (k === "reason" ? cancelReasonLabel(v, lang) : undefined) ?? riderStatusLabel(v, lang) ?? orderStatusLabel(v, lang) ?? payMethodLabel(v, lang) ?? pick(VALUE, v, lang) ?? activityResultLabel(v, lang) ?? (/^[a-z]+(_[a-z]+)*$/.test(v) ? activityFieldLabel(v, lang) : undefined) ?? v;
    };
    const val = (k, x) => {
        if (SECRET.test(k))
            return CHANGED[l];
        if (Array.isArray(x))
            return x.map((y) => (typeof y === "string" && !OPAQUE_ID.test(y) && !/\s/.test(y) ? (SECRET.test(y) ? `${label(y)}` : label(y)) : val(k, y))).filter(Boolean).join(l === "zh" ? "、" : ", ");
        if (x !== null && typeof x === "object") {
            const o = x;
            if ("from" in o && "to" in o && Object.keys(o).length === 2)
                return FROM_TO(scalar(k, o.from), scalar(k, o.to), lang);
            if (typeof o.label === "string")
                return o.extra_charge != null && Number(o.extra_charge) ? `${o.label} (+RM ${Number(o.extra_charge).toFixed(2)})` : o.label;
            return Object.entries(o).filter(([kk, y]) => !ID_KEY.test(kk) && !(typeof y === "string" && OPAQUE_ID.test(y))).map(([kk, y]) => `${label(kk)}: ${val(kk, y)}`).join(l === "zh" ? "，" : ", ");
        }
        return scalar(k, x);
    };
    return Object.entries(meta)
        .filter(([k, x]) => k === "autoRefundId" ? !!x : !ID_KEY.test(k) && !(typeof x === "string" && OPAQUE_ID.test(x)))
        .map(([k, x]) => `${label(k)}: ${k === "autoRefundId" ? (l === "zh" ? "已自动创建" : l === "ms" ? "dicipta auto" : "created") : val(k, x)}`)
        .join(l === "zh" ? "；" : " · ");
}
//# sourceMappingURL=activityLabels.js.map